import { useMemo } from 'react'
import { parseDateOnly } from '../../../../utils/dateUtils'

function getCurvePath(data, key) {
  if (data.length === 0) return ''
  let path = `M ${data[0].x},${data[0][key]}`
  for (let i = 0; i < data.length - 1; i++) {
    const p0 = data[i]
    const p1 = data[i + 1]
    const cp1x = p0.x + (p1.x - p0.x) / 2
    path += ` C ${cp1x},${p0[key]} ${cp1x},${p1[key]} ${p1.x},${p1[key]}`
  }
  return path
}

export function useBalanceProjectionChart({
  balanceProjectionData,
  dailySalesProjectionActive,
  dailySalesAmount,
  purchaseOutflowEvents
}) {
  const balanceProjection = useMemo(() => {
    if (!balanceProjectionData?.days || balanceProjectionData.days.length === 0) {
      return { chartData: [], maxVal: 1, hasSalesProjection: false, hasPurchaseProjection: false }
    }

    const rawDays = balanceProjectionData.days
    const todayOnly = new Date()
    todayOnly.setHours(0, 0, 0, 0)

    const dailyAmount = dailySalesProjectionActive
      ? parseFloat(String(dailySalesAmount).replace(/\./g, '').replace(',', '.')) || 0
      : 0
    const applySalesProjection = dailyAmount > 0

    const getDayDate = (day) => {
      if (day.date) return parseDateOnly(day.date)
      if (day.label) {
        const [dd, mm] = day.label.split('/').map(Number)
        if (dd && mm) {
          const year = todayOnly.getFullYear()
          return new Date(year, mm - 1, dd)
        }
      }
      return null
    }

    const isSunday = (date) => date && date.getDay() === 0
    const isFutureDay = (date) => date && date > todayOnly

    let lastHistoricalIndex = -1
    rawDays.forEach((day, i) => {
      const dayDate = getDayDate(day)
      if (dayDate && !isFutureDay(dayDate)) {
        lastHistoricalIndex = i
      }
    })

    const adjustedDays = rawDays.map((day, i) => {
      const baseInflow = Number(day.inflow) || 0
      const baseOutflow = Number(day.outflow) || 0
      const baseBalance = Number(day.balance) || 0
      const dayDate = getDayDate(day)

      let projectedSales = 0
      if (applySalesProjection && dayDate && isFutureDay(dayDate) && !isSunday(dayDate)) {
        projectedSales = dailyAmount
      }

      return {
        ...day,
        dayDate,
        baseInflow,
        baseOutflow,
        baseBalance,
        projectedSales,
        purchaseOutflow: 0,
        inflow: baseInflow + projectedSales,
        outflow: baseOutflow,
        balance: baseBalance,
        _index: i
      }
    })

    const matchDateToIndex = (dateStr) => {
      const target = parseDateOnly(dateStr)
      if (!target) return -1
      return adjustedDays.findIndex((day) => {
        const dd = day.dayDate || getDayDate(day)
        return dd && dd.getTime() === target.getTime()
      })
    }

    let hasPurchaseProjection = false
    if (purchaseOutflowEvents.length > 0) {
      hasPurchaseProjection = true
      purchaseOutflowEvents.forEach((evt) => {
        const idx = matchDateToIndex(evt.date)
        if (idx >= 0) {
          const current = adjustedDays[idx]
          adjustedDays[idx] = {
            ...current,
            outflow: current.outflow + evt.amount,
            purchaseOutflow: (current.purchaseOutflow || 0) + evt.amount
          }
        }
      })
    }

    if (applySalesProjection || hasPurchaseProjection) {
      const startRecalcFrom = lastHistoricalIndex + 1
      if (startRecalcFrom < adjustedDays.length) {
        let runningBalance = lastHistoricalIndex >= 0
          ? adjustedDays[lastHistoricalIndex].balance
          : Number(balanceProjectionData.initialBalance) || 0

        for (let i = startRecalcFrom; i < adjustedDays.length; i++) {
          const d = adjustedDays[i]
          runningBalance += d.inflow - d.outflow
          adjustedDays[i] = { ...d, balance: runningBalance }
        }
      }
    }

    const allValues = adjustedDays.flatMap((d) => [d.balance, d.inflow, d.outflow])
    const range = Math.max(...allValues.map((v) => Math.abs(v)), 1) * 1.1 || 1
    const divisor = Math.max(adjustedDays.length - 1, 1)

    const scaledData = adjustedDays.map((d, i) => ({
      ...d,
      x: (i / divisor) * 1000,
      yInflow: 300 - (d.inflow / range) * 260,
      yOutflow: 300 - (d.outflow / range) * 260,
      yBalance: 300 - (d.balance / range) * 260
    }))

    return {
      chartData: scaledData,
      maxVal: range,
      minVal: -range,
      hasSalesProjection: applySalesProjection,
      hasPurchaseProjection
    }
  }, [
    balanceProjectionData,
    dailySalesProjectionActive,
    dailySalesAmount,
    purchaseOutflowEvents
  ])

  const inflowPath = useMemo(
    () => getCurvePath(balanceProjection.chartData, 'yInflow'),
    [balanceProjection.chartData]
  )
  const outflowPath = useMemo(
    () => getCurvePath(balanceProjection.chartData, 'yOutflow'),
    [balanceProjection.chartData]
  )
  const balancePath = useMemo(
    () => getCurvePath(balanceProjection.chartData, 'yBalance'),
    [balanceProjection.chartData]
  )

  return { balanceProjection, inflowPath, outflowPath, balancePath }
}
